import * as PDFJS from 'pdfjs-dist';

import {RequesterCaseOptionTypes} from 'appRedux/actions/requestCase/types';
import {FieldOptionType} from 'appRedux/actions/forms/types';

import {OPTIONS_SEPARATOR} from 'pages/admin/updateForm/partials/FormStructure/helper';

import {base642Buffer} from 'helpers/cryptoApiHelper';
import {convertDateToCurrentLocale} from 'helpers/dateTimeConvertingHelper';

globalThis.pdfjsWorker = null;
PDFJS.GlobalWorkerOptions.workerSrc = `/js/pdf.worker.min.js`;

const THUMBNAIL_HEIGHT = 150;
const THUMBNAIL_WIDTH = 150;

export const APPLICATION_MS_WORD = 'application/msword';
export const APPLICATION_OCTET_STREAM = 'application/octet-stream';
export const APPLICATION_PDF = 'application/pdf';
export const APPLICATION_PDG = 'application/pdg';
export const APPLICATION_X_PDF = 'application/x-pdf';
export const APPLICATION_XML_DOCUMENT = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
export const APPLICATION_XML_SPREADSHEET = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export const APPLICATION_VND_TEXT = 'application/vnd.oasis.opendocument.text';
export const APPLICATION_VND_EXCEL = 'application/vnd.ms-excel';
export const APPLICATION_ZIP = 'application/zip';

export const TEXT_CSV = 'text/csv';
export const TEXT_PLAIN = 'text/plain';
export const TEXT_RICH_TEXT = 'text/richtext';
export const TEXT_RTF = 'text/rtf';

export const IMAGE_GIF = 'image/gif';
export const IMAGE_HEIC = 'image/heic';
export const IMAGE_HEIF = 'image/heif';
export const IMAGE_JPEG = 'image/jpeg';
export const IMAGE_MICROSOFT_ICON = 'image/vnd.microsoft.icon';
export const IMAGE_PNG = 'image/png';
export const IMAGE_SVG_XML = 'image/svg+xml';
export const IMAGE_WEBP = 'image/webp';
export const IMAGE_X_ICON = 'image/x-icon';

export const VIDEO_MP4 = 'video/mp4';
export const VIDEO_QUICKTIME = 'video/quicktime';
export const VIDEO_WEBM = 'video/webm';
export const VIDEO_X_MS_ACF = 'video/x-ms-asf';
export const VIDEO_X_MS_VIDEO = 'video/x-msvideo';
export const VIDEO_X_MS_WMV = 'video/x-ms-wmv';

export const isImageFile = (type: string) => {
    return [
        IMAGE_GIF,
        IMAGE_HEIC,
        IMAGE_HEIF,
        IMAGE_JPEG,
        IMAGE_MICROSOFT_ICON,
        IMAGE_PNG,
        IMAGE_SVG_XML,
        IMAGE_WEBP,
        IMAGE_X_ICON,
    ].includes(type);
};

export const isTextFile = (type: string) => {
    return [TEXT_CSV, TEXT_PLAIN, TEXT_RICH_TEXT, TEXT_RTF].includes(type);
};

export const isVideoFile = (type: string) => {
    return [VIDEO_MP4, VIDEO_QUICKTIME, VIDEO_WEBM, VIDEO_X_MS_ACF, VIDEO_X_MS_VIDEO, VIDEO_X_MS_WMV].includes(type);
};

export const createThumbnail = async (file: File): Promise<string | null> => {
    if (!isVideoFile(file.type) && !isImageFile(file.type) && file.type !== APPLICATION_PDF) {
        return null;
    }

    if (file.type === APPLICATION_PDF) {
        const buffer = await getBuffer(file);
        const pdf = await PDFJS.getDocument(buffer).promise;
        const page = await pdf.getPage(1);
        const canvas = document.createElement('canvas');
        const canvasContext = canvas.getContext('2d');

        if (!canvasContext) return null;
        canvas.width = THUMBNAIL_WIDTH;
        canvas.height = THUMBNAIL_HEIGHT;

        const scaleX = canvas.width / page.view[2];
        const scaleY = canvas.height / page.view[3];
        const scale = Math.max(scaleX, scaleY);
        const viewport = page.getViewport({scale});

        await page.render({canvasContext, viewport}).promise;

        return canvas.toDataURL(IMAGE_JPEG);
    } else if (isVideoFile(file.type)) {
        return new Promise((resolve, reject) => {
            const player = document.createElement('video');
            player.setAttribute('src', URL.createObjectURL(file));
            player.load();
            player.addEventListener('error', (err: any) => reject(`${file?.name} is invalid video format.`));

            player.addEventListener('loadedmetadata', () => {
                if (player.duration < 0) {
                    reject('The video is too short.');
                    return;
                }

                setTimeout(() => {
                    player.currentTime = 0.0;
                }, 500);

                player.addEventListener('seeked', () => {
                    const videoCanvas = document.createElement('canvas');
                    videoCanvas.width = THUMBNAIL_WIDTH;
                    videoCanvas.height = THUMBNAIL_HEIGHT;

                    const videoContext = videoCanvas.getContext('2d');
                    if (!videoContext) return null;

                    const scaleX = videoCanvas.width / player.videoWidth;
                    const scaleY = videoCanvas.height / player.videoHeight;
                    const scale = Math.max(scaleX, scaleY);

                    const imgWidth = player.videoWidth * scale;
                    const imgHeight = player.videoHeight * scale;

                    videoContext.drawImage(
                        player,
                        videoCanvas.width / 2 - imgWidth / 2,
                        videoCanvas.height / 2 - imgHeight / 2,
                        imgWidth,
                        imgHeight,
                    );

                    resolve(videoCanvas.toDataURL(IMAGE_JPEG));
                });
            });
        });
    } else {
        return new Promise(resolve => {
            const img = document.createElement('img');

            getBase64Uri(file)
                .then(dataUri => {
                    img.src = dataUri;

                    img.addEventListener('load', () => {
                        const canvas = document.createElement('canvas');
                        const context = canvas.getContext('2d');

                        canvas.width = THUMBNAIL_WIDTH;
                        canvas.height = THUMBNAIL_HEIGHT;

                        const scaleX = canvas.width / img.width;
                        const scaleY = canvas.height / img.height;
                        const scale = Math.max(scaleX, scaleY);

                        const imgWidth = img.width * scale;
                        const imgHeight = img.height * scale;

                        context?.drawImage(
                            img,
                            canvas.width / 2 - imgWidth / 2,
                            canvas.height / 2 - imgHeight / 2,
                            imgWidth,
                            imgHeight,
                        );

                        canvas.remove();
                        img.remove();

                        resolve(canvas.toDataURL(IMAGE_JPEG));
                    });
                })
                .catch(() => resolve(null));
        });
    }
};

export const base64ToFile = (base64String: string) => {
    const arrayBuffer = base642Buffer(base64String.split(',')[1]);
    const blob = new Blob([arrayBuffer], {type: IMAGE_JPEG});
    return blob;
};

export const getBase64Uri = (file: File): Promise<string> => {
    const reader = new FileReader();

    return new Promise((resolve, reject) => {
        reader.onload = function () {
            typeof reader.result === 'string' ? resolve(reader.result) : reject();
        };
        reader.onerror = function (error) {
            reject(error);
        };
        reader.readAsDataURL(file);
    });
};

const getBuffer = (file: File): Promise<ArrayBuffer> => {
    const reader = new FileReader();

    return new Promise((resolve, reject) => {
        reader.onload = function () {
            reader.result && typeof reader.result !== 'string' ? resolve(reader.result) : reject();
        };
        reader.onerror = function (error) {
            reject(error);
        };
        reader.readAsArrayBuffer(file);
    });
};

export const isRequesterCaseOptionPresented = (
    optionId: number,
    requesterCaseOptions: RequesterCaseOptionTypes[],
): boolean => {
    return requesterCaseOptions.some(item => item.optionId === optionId);
};

export const getInitialSelectedOption = (
    fieldId: number,
    requesterCaseOptions: RequesterCaseOptionTypes[],
): RequesterCaseOptionTypes | undefined => {
    return requesterCaseOptions.find(item => item.fieldId === fieldId);
};

export const getTextFieldLabel = (
    label: string,
    translatedLabel: string,
    previewMode?: boolean,
    isCustomOption?: boolean,
): string => {
    if (isCustomOption) return '';
    return previewMode ? label : translatedLabel;
};

export const getOptionsIdsArray = (options: FieldOptionType[]): number[] => {
    const optionsIds: number[] = [];
    options.forEach(item => {
        optionsIds.push(item.id);
    });
    return optionsIds;
};

export const isSelectedOptionCustom = (options: FieldOptionType[], optionId): boolean => {
    return options.some(option => option.id === optionId && option.isCustom);
};

export const getDropdownSelectedInitialValue = (
    options: FieldOptionType[],
    customSelectedOptionId: number | null,
    previewModeSelectedOption: number | null,
    previewMode?: boolean,
    initialSelectedOption?: RequesterCaseOptionTypes,
): string => {
    if (previewMode) {
        return String(previewModeSelectedOption);
    }
    if (customSelectedOptionId) {
        const selectedOption = options.find(option => option.id === customSelectedOptionId);
        return selectedOption ? String(selectedOption.id) : '';
    }
    if (initialSelectedOption) {
        return String(initialSelectedOption.optionId);
    }
    return '';
};

export const getSelectedCustomCheckbox = (
    options: FieldOptionType[],
    requesterCaseOptions: RequesterCaseOptionTypes[],
): FieldOptionType | null => {
    for (let i = 0, n = options.length; i < n; i++) {
        const option = options[i];
        if (isRequesterCaseOptionPresented(option.id, requesterCaseOptions) && option.isCustom) {
            return option;
        }
    }
    return null;
};

export const getCurrentFieldValue = (
    value: string,
    optionsIds?: number[],
    isCustomOption?: boolean,
    isCustomCheckboxField?: boolean,
): string => {
    if (isCustomOption && optionsIds && isCustomCheckboxField) {
        const valueArray = value.split(OPTIONS_SEPARATOR);
        const results: string[] = [];
        valueArray.forEach(item => {
            if (!optionsIds.includes(Number(item))) {
                results.push(item);
            }
        });
        return results.join(OPTIONS_SEPARATOR);
    }
    if (isCustomOption && optionsIds && optionsIds.includes(Number(value))) return '';
    return value;
};

export const getInitialSelectedOptions = (
    options: FieldOptionType[],
    requesterCaseOptions: RequesterCaseOptionTypes[],
    initialValue: string,
): string[] => {
    const initialValuesArray = initialValue ? initialValue.split(OPTIONS_SEPARATOR) : [];
    const customAnswer: string | null = getCustomAnswerFromInitialValue(options, initialValuesArray);
    const results: string[] = [];
    requesterCaseOptions.forEach(caseSelectedOptions => {
        const option = options.find(option => option.id === caseSelectedOptions.optionId);
        if (option && option.isCustom && customAnswer) {
            results.push(customAnswer);
        } else if (option) {
            results.push(String(caseSelectedOptions.optionId));
        }
    });
    return results;
};

export const getCustomAnswerFromInitialValue = (
    options: FieldOptionType[],
    initialValuesArray: string[],
): string | null => {
    const optionsIds: number[] = getOptionsIdsArray(options);
    const results: string[] = [];
    initialValuesArray.forEach(initialValue => {
        if (!optionsIds.includes(Number(initialValue))) {
            results.push(initialValue);
        }
    });
    return results.length > 0 ? results.join(OPTIONS_SEPARATOR) : null;
};
